import { useEffect } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Bid, IconButton } from 'components';
import { Controller } from 'react-hook-form';
import { minServingFields } from 'constants';
import { limitDecimal, formatOnBlur, getErrorFieldName, getNetworkAndDirectBuyNames } from 'utils';
import { validateCPI, validateeCPM } from '../../validation';
import { ErrorMarker, DeleteIconNew } from 'assets/icons';
import * as S from './CountryItem.styled';

const { networkField, networkCountriesError, directBuyField, directBuyCountriesError } =
  getNetworkAndDirectBuyNames(minServingFields);

const getErrorAndMessage = (errors, cca2, field) => {
  let errorField = getErrorFieldName(field, minServingFields);
  const errorObj = errors[errorField];
  const noError = { error: false };

  if (!errorObj) return noError;

  return errorObj.type === 'custom' && errorObj.cca2 === cca2
    ? { error: true, message: errorObj.message }
    : noError;
};

export const CountryItem = ({
  country,
  index,
  control,
  field,
  errors,
  clearErrors,
  handleDeleteCountry,
  setValue,
}) => {
  if (!country) return null;

  const { error: submitErr, message: submitErrMsg } = getErrorAndMessage(
    errors,
    country.cca2,
    field,
  );

  const handleDelete = () => {
    if (submitErr) clearErrors(getErrorFieldName(field, minServingFields));
    handleDeleteCountry(field, country.cca2);
  };

  return (
    <S.Container style={submitErrMsg ? { paddingBottom: '40px' } : {}}>
      <S.Country>
        <ReactCountryFlag countryCode={country?.cca2} svg />
        <S.Label>
          {country.cca2}-{country.name}
        </S.Label>
      </S.Country>
      <>
        {field === networkField && (
          <NetworkItem
            country={country}
            index={index}
            control={control}
            clearErrors={clearErrors}
            submitErr={submitErr}
            submitErrMsg={submitErrMsg}
            setValue={setValue}
          />
        )}
        {field === directBuyField && (
          <DirectBuyItem
            country={country}
            index={index}
            control={control}
            clearErrors={clearErrors}
            submitErr={submitErr}
            setValue={setValue}
          />
        )}
      </>
      <IconButton.SmallSize
        onClick={handleDelete}
        icon={DeleteIconNew}
        type={IconButton.btnTypes.internal}
        style={{ flexShrink: 0 }}
      />
    </S.Container>
  );
};

const NetworkItem = ({
  country,
  index,
  control,
  clearErrors,
  submitErr,
  submitErrMsg,
  setValue,
}) => {
  // forces updating nested controllers
  useEffect(() => {
    setValue(`${networkField}.countries[${index}].minCpi`, country.minCpi || '');
    setValue(`${networkField}.countries[${index}].minEcpm`, country.minEcpm || '');
  }, [country.minCpi, country.minEcpm]);

  return (
    <S.ControllersWithErrorWrap>
      <S.Controllers>
        <Controller
          key={country.cca2 + 'cpi'}
          defaultValue={country?.minCpi || ''}
          name={`${networkField}.countries[${index}].minCpi`}
          control={control}
          rules={validateCPI}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Bid.Small
                label='CPI'
                value={value}
                error={!!error || (submitErr && !+value)}
                labelWidth={65}
                onChange={(e) => {
                  if (submitErr) clearErrors(networkCountriesError);
                  return limitDecimal(e, onChange, 2);
                }}
                onBlur={() => formatOnBlur(value, onChange)}
              />
            );
          }}
        />
        <Controller
          key={country.cca2 + 'espm'}
          defaultValue={country?.minEcpm || ''}
          name={`${networkField}.countries[${index}].minEcpm`}
          control={control}
          rules={validateeCPM}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Bid.Small
              label='eCPM'
              value={value}
              error={!!error || (submitErr && !+value)}
              width={60}
              onBlur={() => formatOnBlur(value, onChange)}
              onChange={(e) => {
                if (submitErr) clearErrors(networkCountriesError);
                return limitDecimal(e, onChange, 3);
              }}
            />
          )}
        />
      </S.Controllers>
      {submitErrMsg && (
        <S.ErrorWrap>
          <ErrorMarker />
          <S.ErrorText>{submitErrMsg}</S.ErrorText>
        </S.ErrorWrap>
      )}
    </S.ControllersWithErrorWrap>
  );
};

const DirectBuyItem = ({
  country,
  index,
  control,
  clearErrors,
  submitErr,
  submitErrMsg,
  setValue,
}) => {
  // forces updating nested controllers
  useEffect(() => {
    setValue(`${directBuyField}.countries[${index}].directCpi`, country.directCpi || '');
  }, [country.directCpi]);

  return (
    <S.ControllersWithErrorWrap>
      <Controller
        key={country.cca2 + 'cpi'}
        defaultValue={country?.directCpi || ''}
        name={`${directBuyField}.countries[${index}].directCpi`}
        control={control}
        rules={validateCPI}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Bid.Small
            label='CPI'
            value={value}
            error={!!error || (submitErr && !+value)}
            onBlur={() => formatOnBlur(value, onChange)}
            onChange={(e) => {
              if (submitErr) clearErrors(directBuyCountriesError);
              return limitDecimal(e, onChange, 2);
            }}
          />
        )}
      />
      {submitErrMsg && (
        <S.ErrorWrap>
          <ErrorMarker />
          <S.ErrorText>{submitErrMsg}</S.ErrorText>
        </S.ErrorWrap>
      )}
    </S.ControllersWithErrorWrap>
  );
};
